/**
 * La malla neuronal del inicio de smarty.lat: una retícula hexagonal con pulsos que recorren las aristas y
 * nodos que se encienden alrededor del cursor. Es la misma de `landing-corporativa.html` (#malla-hero),
 * pasada a componente para que el login se vea de la misma familia que la página de inicio.
 *
 * ⭐ LOS COLORES SALEN DE VARIABLES CSS del contenedor (`--malla-linea`, `--malla-pulso`, `--malla-pulso-2`),
 *    así sigue al claro y al oscuro sin código aparte, igual que en la página de inicio.
 * 🪤 EL DESVÍO DE CADA NODO ES DETERMINISTA (sale de su fila y columna, no de Math.random): la malla queda
 *    igual en cada carga y no «salta» al cambiar el tamaño de la ventana.
 * 🪤 SE DETIENE CUANDO NO SE VE: con la pestaña oculta un requestAnimationFrame sólo gasta batería. Con
 *    «reducir movimiento» se pinta un solo cuadro, quieto.
 */
import { useEffect, useRef } from 'react';

interface Nodo {
  x: number;
  y: number;
  /** Fase de la respiración del nodo. */
  p: number;
}
interface Arista {
  a: Nodo;
  b: Nodo;
}
interface Pulso extends Arista {
  t: number;
  sp: number;
}

const RADIO = 54;
/** Alrededor del cursor, hasta dónde se encienden los nodos. */
const ALCANCE_DEL_CURSOR = 150;

export function MallaNeuronal({ className }: { className?: string }) {
  const lienzo = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = lienzo.current;
    const ctx = cv?.getContext('2d');
    const zona = cv?.parentElement;
    if (!cv || !ctx || !zona) return;

    const quieto = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let ancho = 0;
    let alto = 0;
    let cuadro = 0;
    let tic = 0;
    let nodos: Nodo[] = [];
    let aristas: Arista[] = [];
    let pulsos: Pulso[] = [];
    const raton = { x: -9999, y: -9999, on: false };
    let colores = { linea: '#D2D8E2', pulso: '#E2551A', pulso2: '#0A9C86' };
    const leerColores = () => {
      const cs = getComputedStyle(cv);
      colores = {
        linea: cs.getPropertyValue('--malla-linea').trim() || colores.linea,
        pulso: cs.getPropertyValue('--malla-pulso').trim() || colores.pulso,
        pulso2: cs.getPropertyValue('--malla-pulso-2').trim() || colores.pulso2,
      };
    };

    const construir = () => {
      const r = cv.getBoundingClientRect();
      // Tope de 2: en pantallas 3x el lienzo se va a 9x de píxeles sin que se note la mejora.
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      ancho = r.width;
      alto = r.height;
      if (!ancho || !alto) return;
      cv.width = Math.round(ancho * dpr);
      cv.height = Math.round(alto * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      nodos = [];
      aristas = [];
      pulsos = [];
      const hs = ((Math.sqrt(3) * RADIO) / 2) * 1.16;
      const vs = RADIO * 0.9;
      const columnas = Math.ceil(ancho / hs) + 2;
      const filas = Math.ceil(alto / vs) + 2;
      for (let f = -1; f < filas; f++) {
        for (let c = -1; c < columnas; c++) {
          const jx = Math.sin(f * 12.9 + c * 4.1) * 7;
          const jy = Math.cos(c * 7.3 + f * 2.7) * 7;
          nodos.push({ x: c * hs + (f % 2 ? hs / 2 : 0) + jx, y: f * vs + jy, p: ((f * 7 + c * 13) % 628) / 100 });
        }
      }
      for (let i = 0; i < nodos.length; i++) {
        for (let j = i + 1; j < nodos.length; j++) {
          if (Math.hypot(nodos[i].x - nodos[j].x, nodos[i].y - nodos[j].y) < RADIO * 1.05) aristas.push({ a: nodos[i], b: nodos[j] });
        }
      }
      leerColores();
    };

    const pintar = () => {
      tic++;
      // Las variables se releen de vez en cuando: si cambia el tema, la malla lo sigue sin recargar.
      if (tic % 30 === 0) leerColores();
      ctx.clearRect(0, 0, ancho, alto);
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.3;
      ctx.strokeStyle = colores.linea;
      for (const { a, b } of aristas) {
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
      if (tic % 13 === 0 && pulsos.length < 28 && aristas.length) {
        const l = aristas[Math.floor(Math.random() * aristas.length)];
        pulsos.push({ a: l.a, b: l.b, t: 0, sp: 0.012 + Math.random() * 0.02 });
      }
      for (let i = pulsos.length - 1; i >= 0; i--) {
        const pu = pulsos[i];
        pu.t += pu.sp;
        if (pu.t >= 1) {
          pulsos.splice(i, 1);
          continue;
        }
        const x = pu.a.x + (pu.b.x - pu.a.x) * pu.t;
        const y = pu.a.y + (pu.b.y - pu.a.y) * pu.t;
        const fuerza = 1 - Math.abs(pu.t - 0.5) * 1.2;
        ctx.fillStyle = i % 5 === 0 ? colores.pulso2 : colores.pulso;
        ctx.globalAlpha = 0.9 * fuerza;
        ctx.beginPath();
        ctx.arc(x, y, 1.8, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 0.18 * fuerza;
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fill();
      }
      for (const n of nodos) {
        const respira = 0.5 + 0.5 * Math.sin(tic * 0.02 + n.p);
        const enciende = raton.on ? Math.max(0, 1 - Math.hypot(n.x - raton.x, n.y - raton.y) / ALCANCE_DEL_CURSOR) : 0;
        ctx.globalAlpha = 0.26 + respira * 0.24 + enciende * 0.5;
        ctx.fillStyle = enciende > 0.3 ? colores.pulso : colores.linea;
        ctx.beginPath();
        ctx.arc(n.x, n.y, 1.5 + enciende * 2.4, 0, Math.PI * 2);
        ctx.fill();
        if (enciende > 0.2) {
          ctx.globalAlpha = enciende * 0.35;
          ctx.strokeStyle = colores.pulso;
          ctx.beginPath();
          ctx.arc(n.x, n.y, 3 + enciende * 7, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
      ctx.globalAlpha = 1;
    };

    const bucle = () => {
      pintar();
      cuadro = requestAnimationFrame(bucle);
    };
    const detener = () => {
      cancelAnimationFrame(cuadro);
      cuadro = 0;
    };
    const arrancar = () => {
      detener();
      construir();
      if (quieto) pintar();
      else if (!document.hidden) cuadro = requestAnimationFrame(bucle);
    };

    const mover = (e: PointerEvent) => {
      const r = cv.getBoundingClientRect();
      raton.x = e.clientX - r.left;
      raton.y = e.clientY - r.top;
      raton.on = true;
    };
    const salir = () => {
      raton.on = false;
    };
    const alCambiarVisibilidad = () => {
      if (document.hidden) detener();
      else if (!quieto && !cuadro) cuadro = requestAnimationFrame(bucle);
    };
    let espera: ReturnType<typeof setTimeout> | undefined;
    const alCambiarTamano = new ResizeObserver(() => {
      clearTimeout(espera);
      espera = setTimeout(arrancar, 180);
    });

    zona.addEventListener('pointermove', mover);
    zona.addEventListener('pointerleave', salir);
    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    alCambiarTamano.observe(cv);
    arrancar();

    return () => {
      detener();
      clearTimeout(espera);
      alCambiarTamano.disconnect();
      zona.removeEventListener('pointermove', mover);
      zona.removeEventListener('pointerleave', salir);
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
    };
  }, []);

  return <canvas ref={lienzo} className={className} aria-hidden="true" />;
}
